import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { requestOtp, verifyOtp } from "../api";
import { useUser } from "../hooks/useUser";

type Step = "email" | "code";

export function SignInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();

  useEffect(() => {
    if (!userLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, userLoading, navigate]);

  const intentParam = params.get("intent");
  const initialIntent: "creator" | "business" =
    intentParam === "business" ? "business" : "creator";

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [intent, setIntent] = useState<"creator" | "business">(initialIntent);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<
    "idle" | "sending" | "verifying" | "sent" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "code") {
      // Autofocus the code input when it appears so the user can paste/type
      // immediately after switching from their email app.
      codeInputRef.current?.focus();
    }
  }, [step]);

  async function onRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg(null);
    try {
      await requestOtp(email, intent);
      setStatus("sent");
      setStep("code");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "something went wrong");
    }
  }

  async function onVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setStatus("verifying");
    setErrorMsg(null);
    try {
      await verifyOtp(email, code);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setStatus("error");
      const msg = err instanceof Error ? err.message : "verification failed";
      setErrorMsg(
        msg.includes("400") ? "That code is invalid or has expired." : msg,
      );
    }
  }

  async function onResendCode() {
    setStatus("sending");
    setErrorMsg(null);
    setCode("");
    try {
      await requestOtp(email, intent);
      setStatus("sent");
      codeInputRef.current?.focus();
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "something went wrong");
    }
  }

  function backToEmail() {
    setStep("email");
    setCode("");
    setStatus("idle");
    setErrorMsg(null);
  }

  return (
    <div className="mx-auto max-w-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="card"
      >
        {step === "email" ? (
          <>
            <h1 className="text-2xl font-semibold tracking-tightish">Sign in</h1>
            <p className="mt-1 text-sm text-muted">
              We'll email you a 6-digit code.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-1.5 rounded-md border border-border bg-paper p-1">
              {(["creator", "business"] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setIntent(opt)}
                  className={`rounded px-3 py-1.5 text-sm font-semibold transition-colors ${
                    intent === opt
                      ? "bg-surface text-ink shadow-xs"
                      : "text-muted hover:text-ink"
                  }`}
                >
                  {opt === "creator" ? "Creator" : "Business"}
                </button>
              ))}
            </div>

            <form onSubmit={onRequestCode} className="mt-4 grid gap-3">
              <input
                type="email"
                required
                placeholder="you@neighborhood.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                autoComplete="email"
                inputMode="email"
              />
              <button
                type="submit"
                disabled={status === "sending"}
                className="btn-primary"
              >
                {status === "sending" ? "Sending…" : "Email me a code"}
              </button>
            </form>

            {status === "error" && (
              <p className="mt-4 text-sm text-rose">{errorMsg ?? "Failed to send"}</p>
            )}
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold tracking-tightish">
              Enter your code
            </h1>
            <p className="mt-1 text-sm text-muted">
              We sent a 6-digit code to{" "}
              <span className="font-semibold text-ink">{email}</span>. It
              expires in 10 minutes.
            </p>

            <form onSubmit={onVerifyCode} className="mt-5 grid gap-3">
              <input
                ref={codeInputRef}
                type="text"
                required
                placeholder="123456"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="input text-center font-mono text-2xl tracking-[0.4em]"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                aria-label="6-digit sign-in code"
              />
              <button
                type="submit"
                disabled={status === "verifying" || code.length !== 6}
                className="btn-primary"
              >
                {status === "verifying" ? "Verifying…" : "Sign in"}
              </button>
            </form>

            {status === "error" && (
              <p className="mt-4 text-sm text-rose">{errorMsg}</p>
            )}

            <div className="mt-5 flex items-center justify-between text-xs text-muted">
              <button
                type="button"
                onClick={backToEmail}
                className="font-semibold text-ink hover:underline"
              >
                ← Use a different email
              </button>
              <button
                type="button"
                onClick={onResendCode}
                disabled={status === "sending"}
                className="font-semibold text-ink hover:underline disabled:opacity-50"
              >
                {status === "sending" ? "Sending…" : "Resend code"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
